import List from "@mui/material/List";

import React, { useMemo, useState } from "react";
import { CheckoutCardContent, CheckoutCardSubHeader } from "./CheckoutCard";
import { Trans, useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import FormikTextField from "../../../components/Common/FormikTextField";
import { getYUPValidators } from "../../../components/yupvalidators";
import ListItemRadioButton from "../../../components/Common/ListItemRadioButton";
import { useCheckout } from "../../providers/CheckoutProvider";
import { MenuItem, TextField } from "@mui/material";

const DigitalDeliveryOption = ({ deliveryMethod }) => {
  // Read `user` from the checkout (not AuthProvider): the checkout endpoint
  // always returns a user with an order address, including the anonymous
  // pre-order flow where the AuthProvider user has no address. This matches
  // PostageDeliveryOption and lets us pre-fill postal code / state.
  const { options, deliveryOptionData, user, setDeliveryOption } =
    useCheckout();
  const { t } = useTranslation();

  return (
    <>
      <Formik
        initialValues={{
          name: deliveryOptionData?.contactInfo?.name || user.name || "",
          emailAddress:
            deliveryOptionData?.contactInfo?.emailAddress ||
            user.emailAddress ||
            "",
          postalCode:
            deliveryOptionData?.contactInfo?.postalCode ||
            user.address?.postalCode ||
            "",
          state:
            deliveryOptionData?.contactInfo?.state ||
            user.address?.state ||
            "",
        }}
        validateOnMount={true}
        validationSchema={schema(t, options.useSalesTax, options.gatherState)}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          const data = {
            contactInfo: { ...values },
          };
          console.log("DeliveryMethod", deliveryMethod);
          setDeliveryOption(deliveryMethod.deliveryOptions[0], data);
        }}
      >
        {({ values, setFieldValue, isValid, isSubmitting }) => {
          return (
            <Form data-testid="digital-delivery-option">
              <CheckoutCardContent>
                <CheckoutCardSubHeader>
                  <Trans>Contact details</Trans>
                </CheckoutCardSubHeader>

                <Field
                  component={FormikTextField}
                  label={t("Your name")}
                  name="name"
                  data-testid="digital-delivery-name"
                />

                <Field
                  component={FormikTextField}
                  label={t("Your email address")}
                  name="emailAddress"
                  data-testid="digital-delivery-email"
                />

                {options.useSalesTax && (
                  <>
                    <Field
                      component={FormikTextField}
                      label={t("Postal code")}
                      name="postalCode"
                    />
                    {options.gatherState && !options.states && (
                      <Field
                        id="shipping-form-state"
                        component={FormikTextField}
                        label={t("State")}
                        name="state"
                      />
                    )}

                    {options.gatherState && options.states && (
                      <Field
                        id="shipping-form-state"
                        select
                        component={FormikTextField}
                        label={t("State")}
                        name="state"
                        value={values.state}
                      >
                        {Object.keys(options.states)
                          .filter((key) => key !== "")
                          .map((key) => {
                            const value = options.states[key];
                            return (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            );
                          })}
                      </Field>
                    )}
                  </>
                )}

                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                    type="submit"
                  >
                    <Trans>Next step</Trans>
                  </Button>
                </Box>
              </CheckoutCardContent>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default DigitalDeliveryOption;

const schema = (t, useSalesTax, gatherState) => () => {
  const validators = getYUPValidators(t);
  return Yup.object().shape({
    name: validators.FULL_NAME.required(t("This is required!")),
    emailAddress: validators.EMAIL_ADDRESS.required(t("This is required!")),
    postalCode: Yup.string().when([], {
      is: () => useSalesTax,
      then: (s) => s.required(t("This is required!")),
      otherwise: (s) => s.notRequired(),
    }),
    state: Yup.string().when([], {
      is: () => useSalesTax && gatherState,
      then: (s) => s.required(t("This is required!")),
      otherwise: (s) => s.notRequired(),
    }),
  });
};
